import { produce } from 'immer';
import { Store } from './rootReducer';
import { of, from, Observable } from 'rxjs';
import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { endpoints } from 'endpoints.config';
import {
  Scopes,
  loadTokenResource,
  saveTokenResource,
  setAuthCookie,
  clearAuthCookie,
} from 'helpers/auth';
import { hasExpiredSession } from 'helpers/session';
import { SESSION_EXPIRED_MESSAGE } from 'appConstants';
import api from 'api';
import {
  map,
  tap,
  switchMap,
  catchError,
  ignoreElements,
} from 'rxjs/operators';
import { combineEpics, ofType, ActionsObservable } from 'redux-observable';
import { createSelector } from 'reselect';
import { addSeconds } from 'date-fns';
import { navigate } from '@reach/router';
import { AppPath } from 'appConstants';

export type SignInAttempt = {
  emailAddress: string;
  password: string;
  rememberMe: boolean;
};

export type AutoSignInAttempt = {
  token: string;
};

type SignInError = {
  error: null | string;
  error_description: string;
  error_uri: null;
};

type SignInOkay = {
  details: {
    accessToken: string;
    expiresIn: string;
    refreshToken: string;
    refreshTokenExpiresIn: string;
    scopes?: Scopes;
    tokenType: string;
    profileInitials: string;
  };
  status: string;
};

/** An admin generated this user's password, so they must set their own before they get any tokens. */
type PasswordChangeRequired = {
  details: {
    bPasswordChangeRequired: true;
    passwordChangeToken: string;
  };
  status: string;
};

export type SignInResponse = SignInOkay | PasswordChangeRequired | SignInError;

const isPasswordChangeRequired = (
  r: SignInResponse
): r is PasswordChangeRequired =>
  (r as PasswordChangeRequired).details?.bPasswordChangeRequired === true;

const isResponseOkay = (r: SignInResponse): r is SignInOkay =>
  !isPasswordChangeRequired(r) &&
  ((r as SignInOkay).status === 'Success' || (r as SignInOkay).status === '1');

/* STATE */
type AuthStatus = 'signed_out' | 'signed_in' | 'pending';
type State = {
  appVisible: boolean;
  authStatus: AuthStatus;
  message: string;
  profileInitials: string;
};

const initialState: State = {
  authStatus: 'signed_out',
  appVisible: false,
  message: '',
  profileInitials: '',
};

/* ACTIONS */

const SIGN_OUT = 'app/auth/SIGN_OUT';
const SIGN_IN = 'app/auth/SIGN_IN';
const SIGN_IN_OKAY = 'app/auth/SIGN_IN_OKAY';
const SIGN_IN_ERROR = 'app/auth/SIGN_IN_ERROR';
const AUTO_SIGN_IN = `app/auth/AUTO_SIGN_IN`;
const SHOW_APP = 'app/auth/SHOW_APP';
const CHECK_SIGNED_IN = 'app/auth/CHECK_SIGNED_IN';
const SET_USER_INITIALS = 'app/auth/SET_USER_INITIALS';
const PASSWORD_CHANGE_REQUIRED = 'app/auth/PASSWORD_CHANGE_REQUIRED';

type Action =
  | { type: typeof SIGN_IN; payload: SignInAttempt }
  | { type: typeof AUTO_SIGN_IN; payload: AutoSignInAttempt }
  | { type: typeof SIGN_OUT; message: string; signOutMarker: boolean; endSession?: boolean }
  | { type: typeof CHECK_SIGNED_IN }
  | { type: typeof SHOW_APP; payload: boolean }
  | { type: typeof SIGN_IN_OKAY; payload: SignInOkay }
  | { type: typeof SIGN_IN_ERROR; payload: SignInError }
  | { type: typeof PASSWORD_CHANGE_REQUIRED; passwordChangeToken: string }
  | {
      type: typeof SET_USER_INITIALS;
      payload: string;
    };

/* REDUCER */

export default function reducer(state = initialState, action: Action): State {
  return produce(state, (draft) => {
    switch (action.type) {
      case SHOW_APP:
        draft.appVisible = true;
        // Is the user already signed in?
        if (action.payload === true) draft.authStatus = 'signed_in';
        break;
      case SIGN_OUT:
        draft.authStatus = 'signed_out';
        draft.message = action.message;
        draft.profileInitials = '';
        break;
      case SIGN_IN:
        draft.authStatus = 'pending';
        draft.message = '';
        break;
      case AUTO_SIGN_IN:
        draft.authStatus = 'pending';
        draft.message = '';
        break;
      case SIGN_IN_OKAY:
        draft.authStatus = 'signed_in';
        draft.profileInitials = action.payload.details.profileInitials;
        break;
      case SIGN_IN_ERROR:
        draft.authStatus = 'signed_out';
        draft.message = action.payload.error_description;
        break;
      case PASSWORD_CHANGE_REQUIRED:
        draft.authStatus = 'signed_out';
        draft.message = '';
        break;
      case SET_USER_INITIALS:
        draft.profileInitials = action.payload;
        break;
    }
  });
}

/* EPICS */

function makeRequest<T>(config: AxiosRequestConfig) {
  return from(api.request<T>(config));
}

function saveResponse(r: SignInResponse) {
  if (isResponseOkay(r)) {
    const {
      accessToken,
      refreshToken,
      expiresIn,
      scopes,
      refreshTokenExpiresIn,
      profileInitials,
    } = r.details;
    const refreshTokenExpires = addSeconds(
      new Date(),
      parseInt(refreshTokenExpiresIn, 10)
    );
    const tokenExpires = addSeconds(new Date(), parseInt(expiresIn, 10));

    saveTokenResource({
      accessToken,
      refreshToken,
      tokenExpires,
      refreshTokenExpires: refreshTokenExpires,
      scopes: scopes ?? [],
      profileInitials,
    });
  }
}

function checkResponse(r: SignInResponse) {
  if (isPasswordChangeRequired(r))
    return passwordChangeRequired(r.details.passwordChangeToken);
  if (isResponseOkay(r)) return signInOkay(r);
  return signInError(r);
}

const parseApiErrorMessageCode = (code: string): string => {
  switch (code) {
    case 'Email_Address_Incorrect':
      return 'Invalid email or password.';
    case 'Password_Incorrect':
      return 'Invalid email or password.';
    case 'Account_Locked':
      return 'Your account has been locked after too many failed attempts. Please try again in 10 minutes.';
    case 'Account_Suppressed':
      return 'Your account is currently inactive.';
    case 'Invalid_Token':
      return 'Invalid token, please try to login. ';
    default:
      return 'Oops, something went wrong. Please try again later.';
  }
};

/** Attempt to sign in and handle response, cancel if we sign out while a request pending. */
const signInEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(SIGN_IN),
    switchMap((attempt) =>
      makeRequest<SignInResponse>({
        url: endpoints.auth.login,
        data: attempt.payload,
        method: 'post',
      }).pipe(
        tap((res) => {
          if (isPasswordChangeRequired(res.data)) return;
          saveResponse(res.data);
          setAuthCookie(res.data);
        }),
        map((res) => checkResponse(res.data)),
        catchError((err: AxiosError) => {
          const errorMessage = parseApiErrorMessageCode(
            err.response?.data.errors &&
              err.response.data.errors.length &&
              err.response.data.errors.length > 0
              ? err.response?.data.errors[0].messageCode
              : ''
          );
          return of(
            signInError({
              error: null,
              error_description: `${errorMessage}`,
              error_uri: null,
            })
          );
        })
        //takeUntil(action$.pipe(ofType(SIGN_OUT, SIGN_IN_ERROR)))
      )
    )
  );

/** Send the user to set their own password. The page posts the token back, so it goes in the link. */
const passwordChangeRequiredEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(PASSWORD_CHANGE_REQUIRED),
    tap((action) => {
      navigate(
        `${AppPath.SET_PASSWORD}?token=${encodeURIComponent(
          action.passwordChangeToken
        )}`,
        { state: { passwordChangeRequired: true } }
      );
    }),
    ignoreElements()
  );

const autoSignInEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(AUTO_SIGN_IN),
    switchMap((attempt) =>
      makeRequest<SignInResponse>({
        // AB#210772 - send the auto-login SSO token in the POST body, not the URL query string
        url: endpoints.auth.authLogin,
        data: { token: attempt.payload.token },
        method: 'post',
      }).pipe(
        tap((res) => {
          saveResponse(res.data);
          setAuthCookie(res.data);
        }),
        map((res) => checkResponse(res.data)),
        catchError((err: AxiosError) => {
          const errorMessage = parseApiErrorMessageCode(
            err.response?.data.errors &&
              err.response.data.errors.length &&
              err.response.data.errors.length > 0
              ? err.response?.data.errors[0].messageCode
              : ''
          );
          return of(
            signInError({
              error: null,
              error_description: `${errorMessage}`,
              error_uri: null,
            })
          );
        })
        //takeUntil(action$.pipe(ofType(SIGN_OUT, SIGN_IN_ERROR)))
      )
    )
  );

/** Clear the local storage on sign out, so we don't appear logged in on refresh. */
const signOutEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(SIGN_OUT),
    tap((action) => {
      // The API ends the portal session and clears the PE and Music sign-in cookie, which the browser won't
      // let this script remove. If the call fails the local sign-out still goes ahead.
      if (action.endSession) {
        axios.post(endpoints.auth.logout, {}, { withCredentials: true }).catch(() => {});
      }
      const check = localStorage.getItem('SP');
      window && window.localStorage.clear();
      check && localStorage.setItem('SP', check);
      if (action.signOutMarker) {
        window && window.localStorage.setItem('signOutAlert', 'true');
      }
      clearAuthCookie();
    }),
    ignoreElements()
  );

const checkSignedInEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(CHECK_SIGNED_IN),
    switchMap(() => {
      // Read first: loadTokenResource clears an expired session's markers.
      const expired = hasExpiredSession();
      const signedIn = loadTokenResource() !== null;
      return expired
        ? of(showApp(false), signOut(SESSION_EXPIRED_MESSAGE))
        : of(showApp(signedIn));
    })
  );

export const authEpic = combineEpics(
  signInEpic,
  passwordChangeRequiredEpic,
  autoSignInEpic,
  signOutEpic,
  checkSignedInEpic
);

/* ACTION CREATORS */

export const signIn = (payload: SignInAttempt): Action => ({
  type: SIGN_IN,
  payload,
});

export const autoSignIn = (payload: AutoSignInAttempt): Action => ({
  type: AUTO_SIGN_IN,
  payload,
});

export const signOut = (message = '', marker = true): Action => ({
  type: SIGN_OUT,
  message,
  signOutMarker: marker,
});

/** The user chose to sign out, so the session also ends on the API and on the PE and Music sites. */
export const userSignOut = (): Action => ({
  ...signOut(),
  endSession: true,
} as Action);

export const checkSignedIn = (): Action => ({
  type: CHECK_SIGNED_IN,
});

const signInOkay = (payload: SignInOkay): Action => ({
  type: SIGN_IN_OKAY,
  payload,
});

const signInError = (payload: SignInError): Action => ({
  type: SIGN_IN_ERROR,
  payload,
});

const passwordChangeRequired = (passwordChangeToken: string): Action => ({
  type: PASSWORD_CHANGE_REQUIRED,
  passwordChangeToken,
});

export const showApp = (payload: boolean): Action => ({
  type: SHOW_APP,
  payload,
});

export const setUserInitials = (payload: string): Action => ({
  type: SET_USER_INITIALS,
  payload,
});

/* SELECTORS */

export const selectAuthStatus = (app: Store) => app.auth.authStatus;
export const selectSignedIn = createSelector(
  selectAuthStatus,
  (status) => status === 'signed_in'
);
export const selectAuthMessage = (app: Store) => app.auth.message;
export const selectAppVisible = (app: Store) => app.auth.appVisible;
export const selectUserInitials = (app: Store) => app.auth.profileInitials;
